import { ActionMenuList, ListItem } from '@ds';
import { DSCopyIcon, DSDeleteIcon, DSEditIcon } from '@ds-icons';
import React, { useState } from 'react';
import { useHistory } from 'react-router';
import { DataTypeProvider } from '@devexpress/dx-react-grid';
import { Input } from '@DesignSystem/controllers';
import { BasicModal } from '@DesignSystem/modals';
import { useDispatch } from 'react-redux';
import useCanEditProject from '@API/project/useCanEditProject';
import useDuplicatePanelTemplateMutation from '@/experiment-management-shared/api/library-panel/useDuplicatePanelTemplateMutation';
import useRemovePanelTemplateMutation from '@/experiment-management-shared/api/library-panel/useRemovePanelTemplateMutation';
import { BUILT_IN_CHART_TYPES } from '@/experiment-management-shared/constants';
import styles from './GalleryTableActionsCell.module.scss';
import dashboardChartsActions from '@/actions/dashboardChartsActions';
import alertsUtil from '@/util/alertsUtil';
import { snackbarTypes } from '@/constants/alertTypes';
import ValueFormatterProps = DataTypeProvider.ValueFormatterProps;

export const GalleryTableActionsCell = ({ row }: ValueFormatterProps) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { data: canEdit } = useCanEditProject();
  const [templateName, setTemplateName] = useState('');
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDuplicateModalOpen, setDuplicateModalOpen] = useState(false);

  const {
    mutate: removePanelTemplateMutate,
    isLoading: isRemovePanelTemplateLoading
  } = useRemovePanelTemplateMutation();

  const duplicatePanelTemplateMutation = useDuplicatePanelTemplateMutation();

  const handleEditPanelTemplate = () => {
    dispatch(
      dashboardChartsActions.updateChartForm(
        BUILT_IN_CHART_TYPES.libraryPanel,
        {
          chartName: row.name,
          description: row.description,
          codeVersion: '',
          code: null,
          // instanceName is always equal to chartName. Added for backward compatibility with old viz panels
          instanceName: row.name
        }
      )
    );
    const editTemplateParams = `&chartType=${BUILT_IN_CHART_TYPES.libraryPanel}&templateId=${row.rank.templateId}&isEditMode=true`;
    history.push({ hash: location.hash + editTemplateParams });
  };

  const handleDeleteTemplate = () => {
    removePanelTemplateMutate({
      templateId: row.rank.templateId,
      scope: 'WORKSPACE'
    });
  };

  const handleDuplicateTemplate = () => {
    duplicatePanelTemplateMutation.mutate(
      {
        templateId: row.rank.templateId,
        templateName
      },
      {
        onSuccess: () => {
          dispatch(
            alertsUtil.openSnackbarDialog(
              snackbarTypes.SUCCESS_API_RESPONSE,
              'Successfully duplicated panel.'
            )
          );
          setDuplicateModalOpen(false);
          setTemplateName('');
        }
      }
    );
  };

  const renderDeleteTemplateModal = () => (
    <BasicModal
      open={isDeleteModalOpen}
      onClose={() => setDeleteModalOpen(false)}
      onPrimaryButtonClick={handleDeleteTemplate}
      onSecondaryButtonClick={() => setDeleteModalOpen(false)}
      isPrimaryBtnLoading={isRemovePanelTemplateLoading}
      primaryButtonText="Yes, delete"
      secondaryButtonText="Cancel"
      title="Delete this panel?"
      content={
        <div className={styles.deleteModalContainer}>
          {`The ‘${row.name}’ panel will be deleted from the library and all views where it was added. The panel cannot be recovered.`}
        </div>
      }
    />
  );

  const renderDuplicateTemplateModal = () => (
    <BasicModal
      open={isDuplicateModalOpen}
      onClose={() => {
        setDuplicateModalOpen(false);
        duplicatePanelTemplateMutation.reset();
      }}
      onPrimaryButtonClick={handleDuplicateTemplate}
      onSecondaryButtonClick={() => setDuplicateModalOpen(false)}
      isPrimaryButtonDisabled={templateName === ''}
      isPrimaryBtnLoading={duplicatePanelTemplateMutation.isLoading}
      primaryButtonText="Save"
      secondaryButtonText="Cancel"
      title="Duplicate template"
      content={
        <div className={styles.duplicateTemplateModalContainer}>
          <Input
            invalidMsg={duplicatePanelTemplateMutation.error}
            invalidBorder={duplicatePanelTemplateMutation.isError}
            required
            title="Panel name"
            onChange={setTemplateName}
          />
        </div>
      }
    />
  );

  return (
    <>
      {isDeleteModalOpen && renderDeleteTemplateModal()}
      {isDuplicateModalOpen && renderDuplicateTemplateModal()}

      <ActionMenuList>
        <ActionMenuList.Trigger />
        <ActionMenuList.Body>
          <ListItem onClick={handleEditPanelTemplate}>
            <ListItem.PrefixContainer>
              <ListItem.Icon Icon={<DSEditIcon />} />
            </ListItem.PrefixContainer>
            <ListItem.Text primary="Edit" />
          </ListItem>
          <ListItem onClick={() => setDuplicateModalOpen(true)}>
            <ListItem.PrefixContainer>
              <ListItem.Icon Icon={<DSCopyIcon />} />
            </ListItem.PrefixContainer>
            <ListItem.Text primary="Duplicate" />
          </ListItem>
          <ListItem
            disabled={!canEdit}
            onClick={() => setDeleteModalOpen(true)}
          >
            <ListItem.PrefixContainer>
              <ListItem.Icon Icon={<DSDeleteIcon />} />
            </ListItem.PrefixContainer>
            <ListItem.Text primary="Delete" />
          </ListItem>
        </ActionMenuList.Body>
      </ActionMenuList>
    </>
  );
};
